import { Banner, Button, Dialog, Field, Input } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { FormPage, FormSection } from '../../components/ui/FormPage';
import { DataTable as Table } from '../../components/ui/DataTable';
import { ICONS } from '../../components/ui/icons';
import { useAppToast } from '../../hooks/useAppToast';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useTenant } from '../../contexts/TenantContext';
import { PerfilHeader, ha, quando } from './ui';

interface ApiKeyRow { id: string; name: string; prefix: string; lastUsedAt: string | null; createdAt: string; revokedAt: string | null }

export function TokensPage() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const { can } = useTenant();
  const canManage = can('integrations:manage');
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetchApi('/api/integracoes');
      if (r.ok) setKeys(((await r.json()) as { keys: ApiKeyRow[] }).keys);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { void load(); }, [load]);

  const call = async (url: string, init: RequestInit, ok: string) => {
    setBusy(true);
    try {
      const r = await fetchApi(url, init);
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(body.message || body.error || 'Não foi possível concluir'); return null; }
      toast.success(ok);
      await load();
      return body;
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const body = await call('/api/integracoes/chaves', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }, 'Token criado');
    if (body?.key) { setSecret(body.key); setCreating(false); setName(''); }
  };

  const revoke = async () => {
    if (!revoking) return;
    await call(`/api/integracoes/chaves/${revoking.id}`, { method: 'DELETE' }, 'Token revogado');
    setRevoking(null);
  };

  const copy = (v: string) => { navigator.clipboard?.writeText(v).then(() => toast.success('Copiado')).catch(() => undefined); };

  if (creating) {
    return (
      <FormPage
        backTo="/perfil/tokens"
        backLabel="Tokens de API"
        onBack={() => setCreating(false)}
        title="Criar token"
        description="O token dá acesso à API pública da organização. O valor aparece uma única vez."
        submitLabel="Criar token"
        submitting={busy}
        submitDisabled={name.trim().length < 2}
        onSubmit={() => void create()}
        onCancel={() => setCreating(false)}
      >
        <FormSection title="Identificação">
          <Field label="Nome" description="Para saber onde ele é usado (ex.: n8n, site, ERP)">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="n8n" maxLength={80} />
          </Field>
        </FormSection>
      </FormPage>
    );
  }

  const active = keys.filter((k) => !k.revokedAt);
  const items = active.filter((k) => !query.trim() || k.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div>
      <PerfilHeader
        title="Tokens de API"
        description="Gerencie o acesso da API pública à sua organização."
        actions={canManage ? <Button variant="primary" icon={ICONS.Plus} onClick={() => setCreating(true)}>Criar token</Button> : undefined}
      />
      <div className="mx-auto flex max-w-[1100px] flex-col gap-4 px-6 py-6">
        {!canManage
          ? <Banner variant="alert">Só administradores da organização gerenciam a API e os webhooks.</Banner>
          : <Banner variant="default">Tokens são da organização e valem para a API pública (<code>ick_…</code>). Webhooks continuam em Integrações.</Banner>}
        <Input aria-label="Buscar tokens" placeholder="Buscar…" value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-sm" />
        <Table
          items={items}
          trackBy="id"
          loading={loading}
          empty="Nenhum token criado."
          columnDefinitions={[
            { id: 'name', header: 'Nome', cell: (k: ApiKeyRow) => <span className="font-medium">{k.name}</span> },
            { id: 'prefix', header: 'Prefixo', cell: (k: ApiKeyRow) => <code className="text-xs">{k.prefix}…</code> },
            { id: 'used', header: 'Último uso', cell: (k: ApiKeyRow) => (k.lastUsedAt ? ha(k.lastUsedAt) : '—') },
            { id: 'created', header: 'Criado', cell: (k: ApiKeyRow) => quando(k.createdAt) },
            ...(canManage ? [{ id: 'act', header: '', cell: (k: ApiKeyRow) => <Button variant="secondary-destructive" size="sm" onClick={() => setRevoking(k)}>Revogar</Button> }] : []),
          ]}
        />
        <p className="text-sm text-kumo-subtle">Mostrando {items.length} de {active.length} tokens</p>
      </div>

      <Dialog.Root open={!!secret} onOpenChange={(o: boolean) => { if (!o) setSecret(null); }}>
        <Dialog size="base" className="p-6">
          <Dialog.Title className="mb-4 text-lg font-semibold">Token criado</Dialog.Title>
          <div className="flex flex-col gap-3">
            <Banner variant="alert">Copie agora. Por segurança, o valor não será mostrado de novo.</Banner>
            <Field label="Valor">
              <Input readOnly value={secret ?? ''} onFocus={(e) => e.currentTarget.select()} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" icon={ICONS.Copy} onClick={() => secret && copy(secret)}>Copiar</Button>
              <Button variant="primary" onClick={() => setSecret(null)}>Já copiei</Button>
            </div>
          </div>
        </Dialog>
      </Dialog.Root>

      <DangerConfirmModal
        open={!!revoking}
        title="Revogar token"
        confirmText="Revogar"
        objectName={revoking?.name}
        onCancel={() => setRevoking(null)}
        onConfirm={() => void revoke()}
      >
        O token "{revoking?.name}" para de funcionar na hora. Integrações que o usam deixam de acessar a API.
      </DangerConfirmModal>
    </div>
  );
}

export default TokensPage;
